import { Form as AntdForm, Badge } from 'antd';
import clsx from 'clsx';
import { Children, cloneElement, isValidElement, useEffect } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';

type AntdFormFieldProps = React.ComponentProps<typeof AntdForm.Item>;

export type FormFieldProps<TFieldValues extends FieldValues = FieldValues> = {
  children: React.ReactNode;
  name: FieldPath<TFieldValues>;
  disabled?: boolean;
  overrideFieldOnChange?: (...values: any[]) => void;
} & Omit<AntdFormFieldProps, 'name' | 'rules' | 'validateStatus'>;

// TODO: Support `onBlur` `ref` `reset`
export const FormField = <TFieldValues extends FieldValues = FieldValues>({
  children,
  name,
  disabled,
  help,
  valuePropName,
  overrideFieldOnChange,
  label,
  required,
  className,
  ...props
}: FormFieldProps<TFieldValues>) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control, disabled });
  const form = AntdForm.useFormInstance();

  useEffect(() => {
    form.setFieldValue(name, field.value);
  }, [field.value, form, name]);

  return (
    <AntdForm.Item
      {...props}
      //@ts-expect-error Ant Design form item name type safe is not necessary here
      name={name}
      initialValue={field.value}
      validateStatus={fieldState.invalid ? 'error' : undefined}
      help={fieldState.error?.message ?? help}
      label={
        label && (
          <span className='flex items-center'>
            {label}
            {required && <Badge status='error' className='ml-2' />}
          </span>
        )
      }
      rootClassName={clsx('c-field', className)}
    >
      {Children.map(
        children,
        (child) =>
          isValidElement(child) &&
          cloneElement(child, {
            ...field,
            //@ts-expect-error onChange type safe is not necessary here
            onChange: (...params) => {
              child.props.onChange && child.props.onChange(...params);
              overrideFieldOnChange
                ? overrideFieldOnChange(...params)
                : field.onChange(...params);
            },
            onBlur: () => {
              child.props.onBlur && child.props.onBlur();
              field.onBlur();
            },
            ...(valuePropName && {
              [valuePropName]: field.value,
            }),
          }),
      )}
    </AntdForm.Item>
  );
};
